import React, { useState, useEffect } from 'react';
import { BarChart3, Calendar, Users, CreditCard, Download, Package, Monitor, Eye, X } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import apiService from '../../services/apiService';
import { formatMoney, formatDate } from '../../utils/formatters';
import SucursalFilter from '../../components/filters/SucursalFilter';
import './SalesReportPage.css';

const ReporteVentasMostradorPage = () => {
    const { loading, setLoading, setError, clearError, showToast } = useApp();

    // Estado para filtros
    const [fechaInicio, setFechaInicio] = useState(getTodayDate());
    const [fechaFin, setFechaFin] = useState(getTodayDate());
    const [metodoPago, setMetodoPago] = useState('');
    const [sucursalId, setSucursalId] = useState('');

    // Estado para datos
    const [ventas, setVentas] = useState([]);
    const [resumen, setResumen] = useState(null);
    const [reportePorUsuario, setReportePorUsuario] = useState([]);
    const [reportePorCaja, setReportePorCaja] = useState([]);
    const [productosMasVendidos, setProductosMasVendidos] = useState([]);
    const [activeTab, setActiveTab] = useState('general');

    // Modal de detalle
    const [showDetalleModal, setShowDetalleModal] = useState(false);
    const [ventaDetalle, setVentaDetalle] = useState(null);
    const [loadingDetalle, setLoadingDetalle] = useState(false);

    useEffect(() => {
        loadReportes();
    }, []);

    function getTodayDate() {
        const today = new Date();
        return today.toISOString().split('T')[0];
    }

    const loadReportes = async () => {
        try {
            setLoading(true);
            clearError();

            // Cargar todos los reportes en paralelo
            const [ventasResult, usuariosResult, cajasResult, productosResult] = await Promise.all([
                apiService.getReporteVentasMostrador(fechaInicio, fechaFin, metodoPago, null, null, sucursalId),
                apiService.getReporteVentasMostradorPorUsuario(fechaInicio, fechaFin, sucursalId),
                apiService.getReporteVentasMostradorPorCaja(fechaInicio, fechaFin, sucursalId),
                apiService.getReporteProductosMostradorMasVendidos(fechaInicio, fechaFin, 15, sucursalId)
            ]);

            if (ventasResult.success) {
                setVentas(ventasResult.data.ventas || []);
                setResumen(ventasResult.data.resumen || null);
            }

            if (usuariosResult.success) {
                setReportePorUsuario(usuariosResult.data || []);
            }

            if (cajasResult.success) {
                setReportePorCaja(cajasResult.data || []);
            }

            if (productosResult.success) {
                setProductosMasVendidos(productosResult.data || []);
            }

        } catch (error) {
            setError('Error al cargar reportes de ventas de mostrador');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateReport = () => {
        loadReportes();
    };

    const handleQuickDate = (days) => {
        const today = new Date();
        const pastDate = new Date();
        pastDate.setDate(today.getDate() - days);

        setFechaInicio(pastDate.toISOString().split('T')[0]);
        setFechaFin(today.toISOString().split('T')[0]);
    };

    const handleVerDetalle = async (ventaId) => {
        setLoadingDetalle(true);
        setShowDetalleModal(true);
        try {
            const result = await apiService.getVentasMostradorDetalle(ventaId);
            if (result.success) {
                setVentaDetalle(result.data);
            } else {
                showToast('Error al cargar detalle', 'error');
            }
        } catch (error) {
            showToast('Error al cargar detalle', 'error');
        } finally {
            setLoadingDetalle(false);
        }
    };

    const handleExportReport = () => {
        showToast('Función de exportación en desarrollo', 'info');
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('es-MX', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="sales-report-page">
            {/* Header */}
            <div className="report-header">
                <div className="header-content">
                    <h1 className="page-title">
                        <Monitor size={24} />
                        Reporte de Ventas de Mostrador
                    </h1>
                    <p className="page-description">
                        Análisis detallado de ventas de mostrador por período, usuario, caja y productos
                    </p>
                </div>

                <div className="header-actions">
                    <Button
                        onClick={handleExportReport}
                        variant="outline"
                        disabled={loading || ventas.length === 0}
                    >
                        <Download size={18} />
                        Exportar
                    </Button>
                </div>
            </div>

            {/* Filtros */}
            <div className="report-filters">
                <div className="filters-row">
                    <div className="filter-group">
                        <label className="filter-label">
                            <Calendar size={16} />
                            Fecha Inicio
                        </label>
                        <Input
                            type="date"
                            value={fechaInicio}
                            onChange={(e) => setFechaInicio(e.target.value)}
                            disabled={loading}
                        />
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">
                            <Calendar size={16} />
                            Fecha Fin
                        </label>
                        <Input
                            type="date"
                            value={fechaFin}
                            onChange={(e) => setFechaFin(e.target.value)}
                            disabled={loading}
                        />
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">
                            <CreditCard size={16} />
                            Método de Pago
                        </label>
                        <select
                            className="filter-select"
                            value={metodoPago}
                            onChange={(e) => setMetodoPago(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">Todos</option>
                            <option value="efectivo">Efectivo</option>
                            <option value="tarjeta">Tarjeta</option>
                            <option value="transferencia">Transferencia</option>
                            <option value="mixto">Mixto</option>
                        </select>
                    </div>

                    <SucursalFilter
                        value={sucursalId}
                        onChange={setSucursalId}
                        disabled={loading}
                    />

                    <div className="filter-actions">
                        <Button
                            onClick={handleGenerateReport}
                            variant="primary"
                            disabled={loading}
                        >
                            {loading ? 'Cargando...' : 'Generar Reporte'}
                        </Button>
                    </div>
                </div>

                {/* Quick Filters */}
                <div className="quick-filters">
                    <span className="quick-filters-label">Rápido:</span>
                    <button className="quick-filter-btn" onClick={() => handleQuickDate(0)}>Hoy</button>
                    <button className="quick-filter-btn" onClick={() => handleQuickDate(7)}>7 días</button>
                    <button className="quick-filter-btn" onClick={() => handleQuickDate(30)}>30 días</button>
                    <button className="quick-filter-btn" onClick={() => handleQuickDate(90)}>90 días</button>
                </div>
            </div>

            {/* Resumen Cards */}
            {resumen && (
                <div className="summary-cards">
                    <div className="summary-card">
                        <div className="card-icon blue">
                            <BarChart3 size={24} />
                        </div>
                        <div className="card-content">
                            <span className="card-label">Total Ventas</span>
                            <span className="card-value">{resumen.total_ventas || 0}</span>
                        </div>
                    </div>

                    <div className="summary-card">
                        <div className="card-icon green">
                            <CreditCard size={24} />
                        </div>
                        <div className="card-content">
                            <span className="card-label">Monto Total</span>
                            <span className="card-value">{formatMoney(resumen.total_monto || 0)}</span>
                        </div>
                    </div>

                    <div className="summary-card">
                        <div className="card-icon yellow">
                            <CreditCard size={24} />
                        </div>
                        <div className="card-content">
                            <span className="card-label">Efectivo</span>
                            <span className="card-value">{formatMoney(resumen.total_efectivo || 0)}</span>
                        </div>
                    </div>

                    <div className="summary-card">
                        <div className="card-icon purple">
                            <CreditCard size={24} />
                        </div>
                        <div className="card-content">
                            <span className="card-label">Tarjeta</span>
                            <span className="card-value">{formatMoney(resumen.total_tarjeta || 0)}</span>
                        </div>
                    </div>

                    <div className="summary-card">
                        <div className="card-icon cyan">
                            <CreditCard size={24} />
                        </div>
                        <div className="card-content">
                            <span className="card-label">Transferencia</span>
                            <span className="card-value">{formatMoney(resumen.total_transferencia || 0)}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="report-tabs">
                <button
                    className={`tab-btn ${activeTab === 'general' ? 'active' : ''}`}
                    onClick={() => setActiveTab('general')}
                >
                    <BarChart3 size={18} />
                    Ventas
                </button>
                <button
                    className={`tab-btn ${activeTab === 'usuarios' ? 'active' : ''}`}
                    onClick={() => setActiveTab('usuarios')}
                >
                    <Users size={18} />
                    Por Usuario
                </button>
                <button
                    className={`tab-btn ${activeTab === 'cajas' ? 'active' : ''}`}
                    onClick={() => setActiveTab('cajas')}
                >
                    <Monitor size={18} />
                    Por Caja
                </button>
                <button
                    className={`tab-btn ${activeTab === 'productos' ? 'active' : ''}`}
                    onClick={() => setActiveTab('productos')}
                >
                    <Package size={18} />
                    Productos
                </button>
            </div>

            {/* Tab Content */}
            <div className="report-content">
                {/* Tab: General - Lista de ventas */}
                {activeTab === 'general' && (
                    <div className="report-table-container">
                        {ventas.length === 0 ? (
                            <div className="empty-report">
                                <BarChart3 size={48} />
                                <p>No hay ventas en el período seleccionado</p>
                            </div>
                        ) : (
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th>Folio</th>
                                        <th>Fecha</th>
                                        <th>Usuario</th>
                                        <th>Caja</th>
                                        <th>Cliente</th>
                                        <th>Método Pago</th>
                                        <th style={{ textAlign: 'right' }}>Total</th>
                                        <th style={{ textAlign: 'center' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ventas.map(venta => (
                                        <tr key={venta.id}>
                                            <td>
                                                <span className="folio-badge">{venta.folio}</span>
                                            </td>
                                            <td>{formatDateTime(venta.fecha_creacion)}</td>
                                            <td>{venta.usuario_nombre || '-'}</td>
                                            <td>{venta.caja_nombre || '-'}</td>
                                            <td>{venta.cliente_nombre || 'Público General'}</td>
                                            <td>
                                                <span className={`method-badge ${venta.metodo_pago}`}>
                                                    {venta.metodo_pago}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'right', fontWeight: 600 }}>
                                                {formatMoney(venta.total)}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <button
                                                    className="action-btn"
                                                    onClick={() => handleVerDetalle(venta.id)}
                                                    title="Ver detalle"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {/* Tab: Por Usuario */}
                {activeTab === 'usuarios' && (
                    <div className="report-table-container">
                        {reportePorUsuario.length === 0 ? (
                            <div className="empty-report">
                                <Users size={48} />
                                <p>No hay datos de usuarios</p>
                            </div>
                        ) : (
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th>Usuario</th>
                                        <th style={{ textAlign: 'right' }}>Ventas</th>
                                        <th style={{ textAlign: 'right' }}>Efectivo</th>
                                        <th style={{ textAlign: 'right' }}>Tarjeta</th>
                                        <th style={{ textAlign: 'right' }}>Transferencia</th>
                                        <th style={{ textAlign: 'right' }}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportePorUsuario.map((row, idx) => (
                                        <tr key={idx}>
                                            <td>{row.usuario_nombre}</td>
                                            <td style={{ textAlign: 'right' }}>{row.total_ventas}</td>
                                            <td style={{ textAlign: 'right' }}>{formatMoney(row.total_efectivo)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatMoney(row.total_tarjeta)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatMoney(row.total_transferencia)}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(row.total_monto)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {/* Tab: Por Caja */}
                {activeTab === 'cajas' && (
                    <div className="report-table-container">
                        {reportePorCaja.length === 0 ? (
                            <div className="empty-report">
                                <Monitor size={48} />
                                <p>No hay datos de cajas</p>
                            </div>
                        ) : (
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th>Caja</th>
                                        <th style={{ textAlign: 'right' }}>Ventas</th>
                                        <th style={{ textAlign: 'right' }}>Efectivo</th>
                                        <th style={{ textAlign: 'right' }}>Tarjeta</th>
                                        <th style={{ textAlign: 'right' }}>Transferencia</th>
                                        <th style={{ textAlign: 'right' }}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportePorCaja.map((row, idx) => (
                                        <tr key={idx}>
                                            <td>{row.caja_nombre}</td>
                                            <td style={{ textAlign: 'right' }}>{row.total_ventas}</td>
                                            <td style={{ textAlign: 'right' }}>{formatMoney(row.total_efectivo)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatMoney(row.total_tarjeta)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatMoney(row.total_transferencia)}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(row.total_monto)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {/* Tab: Productos más vendidos */}
                {activeTab === 'productos' && (
                    <div className="report-table-container">
                        {productosMasVendidos.length === 0 ? (
                            <div className="empty-report">
                                <Package size={48} />
                                <p>No hay datos de productos</p>
                            </div>
                        ) : (
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Producto</th>
                                        <th style={{ textAlign: 'right' }}>Cantidad</th>
                                        <th style={{ textAlign: 'right' }}>Veces Vendido</th>
                                        <th style={{ textAlign: 'right' }}>Total Vendido</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {productosMasVendidos.map((row, idx) => (
                                        <tr key={idx}>
                                            <td>{idx + 1}</td>
                                            <td>{row.producto_nombre}</td>
                                            <td style={{ textAlign: 'right' }}>{parseFloat(row.total_cantidad).toFixed(2)}</td>
                                            <td style={{ textAlign: 'right' }}>{row.veces_vendido}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(row.total_vendido)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>

            {/* Modal de Detalle */}
            {showDetalleModal && (
                <Modal
                    isOpen={showDetalleModal}
                    onClose={() => {
                        setShowDetalleModal(false);
                        setVentaDetalle(null);
                    }}
                    title={`Detalle de Venta ${ventaDetalle?.folio || ''}`}
                    size="large"
                >
                    {loadingDetalle ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            Cargando detalle...
                        </div>
                    ) : ventaDetalle ? (
                        <div style={{ padding: '16px 0' }}>
                            {/* Info de la venta */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: '16px',
                                marginBottom: '24px',
                                padding: '16px',
                                background: '#f9fafb',
                                borderRadius: '8px'
                            }}>
                                <div>
                                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Fecha</span>
                                    <p style={{ margin: '4px 0 0', fontWeight: 500 }}>{formatDateTime(ventaDetalle.fecha_creacion)}</p>
                                </div>
                                <div>
                                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Usuario</span>
                                    <p style={{ margin: '4px 0 0', fontWeight: 500 }}>{ventaDetalle.usuario_nombre}</p>
                                </div>
                                <div>
                                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Caja</span>
                                    <p style={{ margin: '4px 0 0', fontWeight: 500 }}>{ventaDetalle.caja_nombre || '-'}</p>
                                </div>
                                <div>
                                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Cliente</span>
                                    <p style={{ margin: '4px 0 0', fontWeight: 500 }}>{ventaDetalle.cliente_nombre || 'Público General'}</p>
                                </div>
                                <div>
                                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Método de Pago</span>
                                    <p style={{ margin: '4px 0 0', fontWeight: 500, textTransform: 'capitalize' }}>{ventaDetalle.metodo_pago}</p>
                                </div>
                                <div>
                                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Total</span>
                                    <p style={{ margin: '4px 0 0', fontWeight: 700, color: '#059669', fontSize: '18px' }}>{formatMoney(ventaDetalle.total)}</p>
                                </div>
                            </div>

                            {/* Detalles de productos */}
                            <h4 style={{ margin: '0 0 12px', color: '#374151' }}>Productos</h4>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f3f4f6' }}>
                                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Producto</th>
                                        <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Cantidad</th>
                                        <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Precio</th>
                                        <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ventaDetalle.detalles?.map((det, idx) => (
                                        <tr key={idx}>
                                            <td style={{ padding: '10px', borderBottom: '1px solid #e5e7eb' }}>
                                                <div>{det.producto_nombre}</div>
                                                {det.codigo_corto && (
                                                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Código: {det.codigo_corto}</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>{parseFloat(det.cantidad).toFixed(2)}</td>
                                            <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>{formatMoney(det.precio_unitario)}</td>
                                            <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>{formatMoney(det.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {ventaDetalle.observaciones && (
                                <div style={{ marginTop: '16px', padding: '12px', background: '#fef3c7', borderRadius: '6px' }}>
                                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#92400e' }}>Observaciones:</span>
                                    <p style={{ margin: '4px 0 0', color: '#78350f' }}>{ventaDetalle.observaciones}</p>
                                </div>
                            )}
                        </div>
                    ) : null}
                </Modal>
            )}
        </div>
    );
};

export default ReporteVentasMostradorPage;
